import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationResult, MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import App from './App';
import FluentUiThemeProvider from './Components/Theme/FluentUiProvider';
import NotificationProvider from './Contexts/NotificationContext';
import { QueryClientProvider } from './Contexts/QueryClientProvider';
import i18n from './i18n';
import './index.css';
import StoreProvider from './store/StoreProvider';
import { loginRequest, publicClientApp, msalInitialized, isTransientAuthError, reportLoginFailure } from './Security/azureAdAuthentication';
import { isMsalHiddenIframe } from './Security/msalEnvironment';
import UserContextProvider from './Contexts/UserContext';
import { appInsights } from './AppInsights';
import { registerCspViolationReporter } from './Security/cspViolationReporter';

registerCspViolationReporter((name, properties) => appInsights.trackEvent({ name, properties: { ...properties } }));

const loginRetryDelayMs = 2000;
const maxLoginRetries = 3;

// Without this component MsalAuthenticationTemplate rethrows authentication errors while rendering,
// which crashes the app and surfaces them as uncaught exceptions in App Insights. Transient MSAL
// errors (a redirect already in progress in this or another tab) are retried instead of reported.
const AuthenticationErrorHandler = ({ error, login }: MsalAuthenticationResult) => {
    const retries = useRef(0);

    useEffect(() => {
        if (!error) {
            return undefined;
        }
        if (isTransientAuthError(error) && retries.current < maxLoginRetries) {
            retries.current += 1;
            const timer = setTimeout(() => {
                login(InteractionType.Redirect, { ...loginRequest }).catch(() => undefined);
            }, loginRetryDelayMs);
            return () => clearTimeout(timer);
        }
        reportLoginFailure(error);
        return undefined;
    }, [error, login]);

    return null;
};

// MSAL loads the redirect URI (this app) in a hidden iframe to renew tokens silently; starting the
// app there only produces duplicate requests and MSAL errors, so leave that frame untouched.
if (!isMsalHiddenIframe()) {
    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    msalInitialized.then(() => root.render(
        <React.StrictMode>
          <MsalProvider instance={publicClientApp}>
            <UserContextProvider>
              <I18nextProvider i18n={i18n}>
                <QueryClientProvider>
                  <StoreProvider>
                    <FluentUiThemeProvider>
                      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}  authenticationRequest={{ ...loginRequest }} errorComponent={AuthenticationErrorHandler}>
                        <NotificationProvider>
                          <App />
                        </NotificationProvider>
                      </MsalAuthenticationTemplate>
                    </FluentUiThemeProvider>
                  </StoreProvider>
                </QueryClientProvider>
                </I18nextProvider>
              </UserContextProvider>
          </MsalProvider>
        </React.StrictMode>,
    ));
}
